import React, { useState, useEffect, useRef } from 'react';
import { useApi } from '../hooks/useApi';
import { API_ENDPOINTS } from '@caro/shared/constants';
import type { UserPhoto } from '@caro/shared/types';

const SLIDE_INTERVAL = 30_000; // 30 seconds
const CROSSFADE_DURATION = 1_000; // 1 second

export function PhotoFrame() {
  const { data: photos } = useApi<UserPhoto[]>(API_ENDPOINTS.USER_PHOTOS);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    if (!photos || photos.length <= 1) return;

    timerRef.current = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % photos.length);
        setIsTransitioning(false);
      }, CROSSFADE_DURATION);
    }, SLIDE_INTERVAL);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [photos]);

  if (!photos || !Array.isArray(photos) || photos.length === 0) return null;

  const safeIndex = currentIndex < photos.length ? currentIndex : 0;
  const currentPhoto = photos[safeIndex];
  if (!currentPhoto?.url) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: -1,
        overflow: 'hidden',
      }}
    >
      <img
        src={currentPhoto.url}
        alt={currentPhoto.description || 'Photo'}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          opacity: isTransitioning ? 0 : 1,
          transition: `opacity ${CROSSFADE_DURATION}ms ease-in-out`,
        }}
      />
      {/* Overlay for readability.
       *  T6.6 / D7: strengthened from 0.25 → 0.55 in the middle band and
       *  0.5 → 0.7 at the bottom (where the description chip sits) so
       *  white text passes ≥ 4.5:1 against pure-white photo backgrounds.
       *  The top stays 0.45 — the greeting/clock chip already has its
       *  own translucent panel, so over-darkening here would just bury
       *  the photo. Note: WCAG was failing on bright photos before this
       *  bump; verify against a deliberately white test image once
       *  manual smoke is reachable. */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background:
            'linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.55) 40%, rgba(0,0,0,0.7) 100%)',
        }}
      />
      {/* Photo metadata */}
      {currentPhoto.description && (
        <div
          style={{
            position: 'absolute',
            bottom: '100px',
            left: '24px',
            right: '24px',
            padding: '8px 12px',
            backgroundColor: 'rgba(0,0,0,0.5)',
            borderRadius: '8px',
            color: 'white',
            fontSize: 'var(--font-body)',
          }}
        >
          {currentPhoto.description}
          {currentPhoto.uploaderName && (
            <span style={{ opacity: 0.7, marginLeft: '8px' }}>
              — {currentPhoto.uploaderName}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
